/**
 * App Settings Constants
 * PAGE_DEFINITIONS and default values - synchronized with AppSettings.tsx
 */

import {
    Home,
    LogIn,
    FileText,
    LayoutDashboard,
    PackageSearch,
    ShoppingCart,
    History,
    UserCircle,
    Medal,
    Wallet,
    // Section icons
    CreditCard,
    Tag,
    Truck,
    Package,
    Megaphone,
    Grid3X3,
    Award,
    Clock,
    Gift,
    Phone,
    Palette,
    ScrollText,
    Coins,
    MessageSquare,
    ShieldCheck,
} from 'lucide-react';
import type { PageDefinition, AppPageSettingsFormValues, SectionConfig, PageKey } from './types';

// Page definitions with field configurations
export const PAGE_DEFINITIONS: PageDefinition[] = [
    {
        id: 'firstPage',
        label: 'First & Login',
        description: 'อัปโหลดโลโก้และภาพพื้นหลังที่จะแสดงในหน้า First page, Login page และ Register page ของแอป.',
        icon: Home,
        fields: [
            {
                name: 'logoImage',
                label: 'โลโก้',
                type: 'file',
                accept: 'image/*,.svg',
                helperText: 'รองรับ PNG/JPG/SVG • แนะนำอัตราส่วน 1:1 (เช่น 200×200, 400×400 px) • โลโก้จะใช้ร่วมกับหน้า Login และ Register',
                previewLabel: 'ตัวอย่างโลโก้',
            },
            {
                name: 'backgroundImage',
                label: 'ภาพพื้นหลัง',
                type: 'file',
                accept: 'image/*',
                helperText: 'รองรับ JPG/PNG • แนะนำอัตราส่วน 9:16 หรือ 9:19.5 (portrait mobile) เช่น 1080×1920 หรือ 1125×2436 px • หากไม่ใส่จะใช้สีพื้นหลัง default',
                previewLabel: 'ตัวอย่างพื้นหลัง',
            },
        ],
    },
    {
        id: 'loginPage',
        label: 'Login Page',
        description: 'ตั้งค่าที่ซิงก์กับ First Page',
        icon: LogIn,
        hidden: true,
        fields: [
            { name: 'logoImage', label: 'โลโก้ (.svg)', type: 'text' },
            { name: 'backgroundImage', label: 'ภาพพื้นหลัง', type: 'text' },
        ],
    },
    {
        id: 'termPage',
        label: 'Terms Page',
        description: 'ปรับแต่งเนื้อหาเงื่อนไขการใช้งานและวันที่อัปเดตล่าสุด.',
        icon: FileText,
        fields: [
            {
                name: 'termsContent',
                label: 'ข้อความเงื่อนไข',
                type: 'textarea',
                rows: 8,
                helperText: 'รองรับ Markdown พื้นฐาน/บรรทัดใหม่.',
            },
            {
                name: 'lastUpdated',
                label: 'อัปเดตล่าสุด (เช่น 2024-03-01)',
                type: 'text',
            },
            {
                name: 'businessPolicyContent',
                label: 'นโยบายทางธุรกิจ',
                type: 'textarea',
                rows: 8,
                helperText: 'รองรับ Markdown พื้นฐาน/บรรทัดใหม่.',
            },
            {
                name: 'businessPolicyLastUpdated',
                label: 'อัปเดตล่าสุด (นโยบายธุรกิจ)',
                type: 'text',
            },
        ],
    },
    {
        id: 'homePage',
        label: 'Home Page',
        description: 'ตั้งค่าภาพ Popup แจ้งข่าวและจัดการโลโก้ผู้สนับสนุนที่แสดงบนหน้าแรก.',
        icon: LayoutDashboard,
        fields: [
            {
                name: 'homePopupImage',
                label: 'ภาพ Popup',
                type: 'file',
                accept: 'image/*',
                helperText: 'อัปโหลดไฟล์ JPG/PNG ขนาด 1200x1600px (หรือสัดส่วน 3:4) สำหรับหน้าต่าง Popup.',
                previewLabel: 'ตัวอย่าง Popup',
                className: 'md:col-span-2',
            },
            {
                name: 'homePopupIntervalHours',
                label: 'รอบการแสดง Popup (ชั่วโมง)',
                type: 'number',
                helperText: 'ค่าเริ่มต้นตาม PRP = 8 ชั่วโมง.',
            },
            {
                name: 'categoryFilterIcons',
                label: 'รูปหมวดหมู่สินค้า',
                type: 'category-icons',
                helperText: 'รองรับ PNG/JPG • แนะนำอัตราส่วน 1:1 (เช่น 200×200 px) • ขนาดไฟล์ไม่เกิน 500KB',
                className: 'md:col-span-2',
                accept: 'image/*',
            },
            {
                name: 'sponsorBrands',
                label: 'แบรนด์ผู้สนับสนุน (3x2 Grid)',
                type: 'brand-icons',
                helperText: 'แสดงแบรนด์ได้สูงสุด 6 แบรนด์ • แนะนำรูปอัตราส่วน 1:1 • ชื่อแบรนด์จะถูกนำไปใช้เป็น Tag สินค้าโดยอัตโนมัติ',
                className: 'md:col-span-2',
                accept: 'image/*',
            },
        ],
    },
    {
        id: 'itemListPage',
        label: 'Item List',
        description: 'ตั้งค่าเริ่มต้นของการเรียงสินค้าและชุดตัวกรอง.',
        icon: PackageSearch,
        hidden: true,
        fields: [
            {
                name: 'defaultSort',
                label: 'การเรียงลำดับเริ่มต้น',
                type: 'text',
                placeholder: 'popular | newest | price_low_high',
            },
            {
                name: 'availableFilters',
                label: 'ตัวกรองที่เปิดใช้ (Tag ต่อบรรทัด)',
                type: 'textarea',
                rows: 3,
            },
            {
                name: 'showOrderCountLabel',
                label: 'ข้อความจำนวนคำสั่งซื้อ',
                type: 'text',
            },
        ],
    },
    {
        id: 'checkoutPage',
        label: 'Checkout',
        description: 'กำหนดส่วนลดและข้อความวิ่งสำหรับขั้นตอนชำระเงิน.',
        icon: ShoppingCart,
        fields: [
            {
                name: 'deliveryDiscount',
                label: 'ส่วนลดการจัดส่ง (บาท)',
                type: 'number',
                helperText: 'กรอกจำนวนเงินที่ต้องการลดจากค่าจัดส่ง',
            },
            {
                name: 'productDiscountPercent',
                label: 'ส่วนลดสินค้าท้ายใบเสร็จ (%)',
                type: 'number',
                helperText: 'กำหนดเปอร์เซ็นต์ที่ตัดลดราคาสินค้ารวมท้ายบิล',
            },
            {
                name: 'promptpayDiscountPercent',
                label: 'ส่วนลด PromptPay (%)',
                type: 'number',
                helperText: 'เช่น ลด 5% เมื่อเลือกจ่ายผ่าน PromptPay',
            },
            {
                name: 'mobileBankingDiscountPercent',
                label: 'ส่วนลด Mobile Banking (%)',
                type: 'number',
                helperText: 'ส่วนลดเมื่อเลือกชำระผ่าน Mobile Banking',
            },
            {
                name: 'codDiscountPercent',
                label: 'ส่วนลดเก็บเงินปลายทาง (COD) (%)',
                type: 'number',
                helperText: 'ตั้งเป็น 0 หากไม่ให้ส่วนลดสำหรับ COD',
            },
            {
                name: 'secretDealDiscountPercent',
                label: 'ส่วนลดดีลลับ (%)',
                type: 'number',
                helperText: 'ส่วนลดพิเศษเฉพาะแคมเปญดีลลับ',
            },
            {
                name: 'tickerMessage',
                label: 'อักษรวิ่ง',
                type: 'text',
                helperText: 'ข้อความประกาศที่เลื่อนท้ายหน้าจอ Checkout',
            },
            {
                name: 'baseShippingCost',
                label: 'ค่าส่งปกติ (บาท)',
                type: 'number',
                helperText: 'ค่าจัดส่งมาตรฐาน (เช่น 50 บาท)',
            },
            {
                name: 'freeShippingThreshold',
                label: 'ยอดขั้นต่ำสำหรับส่งฟรี (บาท)',
                type: 'number',
                helperText: 'เช่น 1000 บาท = ซื้อครบ 1000 บาทจะส่งฟรี (ใส่ 0 ถ้าไม่มีส่งฟรี)',
            },
            {
                name: 'codWalletId',
                label: 'Wallet ID สำหรับ COD',
                type: 'text',
                helperText: 'Wallet ID ที่จะแสดงบนใบปะหน้าเมื่อเป็นการสั่งซื้อแบบ COD รูปแบบ: "Wallet ID : [ค่าที่กรอก]"',
                placeholder: 'เช่น 1234567890',
            },
            {
                name: 'senderName',
                label: 'ชื่อผู้ส่ง (ใบปะหน้า)',
                type: 'text',
                helperText: 'ชื่อบริษัท/ร้านค้าที่จะแสดงในใบปะหน้าพัสดุ',
                placeholder: 'เช่น HYB MASTER SHOP',
            },
            {
                name: 'senderPhone',
                label: 'เบอร์โทรผู้ส่ง',
                type: 'text',
                helperText: 'เบอร์โทรศัพท์ที่จะแสดงในใบปะหน้า',
                placeholder: 'เช่น 02-XXX-XXXX',
            },
            {
                name: 'senderAddress',
                label: 'ที่อยู่ผู้ส่ง',
                type: 'textarea',
                rows: 2,
                helperText: 'ที่อยู่บริษัท/ร้านค้าสำหรับใบปะหน้า (ไม่ต้องใส่เบอร์โทร)',
                className: 'md:col-span-2',
            },
        ],
    },
    {
        id: 'historyPage',
        label: 'History',
        description: 'ตั้งเวลาอัปเดตเลขพัสดุและจัดการการย้ายคำสั่งซื้อเข้าสู่คลังประวัติ.',
        icon: History,
        fields: [
            {
                name: 'statusUpdateHour',
                label: 'เวลาอัปเดตเลขพัสดุ (ชั่วโมง)',
                type: 'number',
                helperText: 'เช่น 20 = 20:00 น. ระบบจะอัปเดตเลขพัสดุในแอปเวลานี้',
            },
            {
                name: 'archiveAfterDays',
                label: 'ย้ายไปแท็บ "ทั้งหมด" หลัง (วัน)',
                type: 'number',
                helperText: 'ครบกำหนดจะถูกย้ายไปอยู่ในแท็บ "ทั้งหมด" ถาวร (ค่าเริ่มต้น 10 วัน)',
            },
            {
                name: 'repeatDealCopy',
                label: 'ข้อความโปรโมชัน "ดีลลับ"',
                type: 'textarea',
                rows: 3,
            },
        ],
    },
    {
        id: 'myPage',
        label: 'MY Page',
        description: 'อัปโหลดโลโก้ผู้สนับสนุนให้เลื่อนวนบนหน้าโปรไฟล์ผู้ใช้.',
        icon: UserCircle,
        fields: [
            {
                name: 'sponsorCarousel',
                label: 'โลโก้ผู้สนับสนุน',
                type: 'multi-image',
                helperText: 'ระบบจะนำโลโก้ทั้งหมดมาเลื่อนโชว์วนบนหน้า MY Page',
                className: 'md:col-span-2',
                accept: 'image/*,.svg',
            },
            {
                name: 'companyAddress',
                label: 'ที่อยู่บริษัท',
                type: 'textarea',
                rows: 3,
                helperText: 'ที่อยู่บริษัทที่จะแสดงในการ์ด "ติดต่อเรา" บนหน้า MY Page',
                className: 'md:col-span-2',
            },
            {
                name: 'companyPhone',
                label: 'เบอร์โทรบริษัท',
                type: 'text',
                helperText: 'เบอร์โทรศัพท์ที่จะแสดงในการ์ด "ติดต่อเรา" บนหน้า MY Page',
            },
            {
                name: 'supportEmail',
                label: 'อีเมลติดต่อ',
                type: 'text',
                helperText: 'อีเมลสำหรับติดต่อสอบถาม (แสดงใน Privacy Policy และ Support page)',
            },
        ],
    },
    {
        id: 'myLevelPage',
        label: 'MY Level',
        description: 'ปรับแต่งเกณฑ์เลื่อนระดับและส่วนลดสมาชิกแต่ละระดับ.',
        icon: Medal,
        fields: [
            {
                name: 'levels',
                label: 'ตั้งค่าระดับสมาชิก',
                type: 'loyalty-levels',
            },
            {
                name: 'earnPointsPerSpend',
                label: 'จำนวนเงินสะสมแต้ม',
                type: 'number',
                hidden: true,
            },
            {
                name: 'earnPointsAmount',
                label: 'คะแนนที่ได้รับ',
                type: 'number',
                hidden: true,
            },
        ],
    },
    {
        id: 'walletTermPage',
        label: 'Wallet Terms',
        description: 'อัปเดตเงื่อนไขการใช้งาน Wallet และขั้นต่ำการเติม.',
        icon: Wallet,
        fields: [
            {
                name: 'walletTerms',
                label: 'ข้อความเงื่อนไข Wallet',
                type: 'textarea',
                rows: 6,
            },
            {
                name: 'minimumTopup',
                label: 'จำนวนเงินเติมขั้นต่ำ',
                type: 'number',
            },
            {
                name: 'supportContact',
                label: 'ช่องทางติดต่อเมื่อมีปัญหา',
                type: 'textarea',
                rows: 3,
            },
            {
                name: 'topupTiers',
                label: 'แพ็กเกจเติมเงิน',
                type: 'topup-tiers',
                helperText: 'ระบุจำนวนเงินที่เติมและเครดิตรวมที่ลูกค้าจะได้รับ เช่น เติม 100 ได้ 110.',
                className: 'md:col-span-2',
            },
            {
                name: 'tickerMessage',
                label: 'ข้อความวิ่ง (Ticker)',
                type: 'text',
                helperText: 'ข้อความประกาศที่จะวิ่งใต้การ์ดยอดเงินคงเหลือในหน้า Wallet',
            },
        ],
    },
];

// Default values for all settings
export const DEFAULT_PAGE_SETTINGS: AppPageSettingsFormValues = {
    firstPage: {
        logoImage: '',
        backgroundImage: '',
    },
    loginPage: {
        logoImage: '',
        backgroundImage: '',
    },
    termPage: {
        termsContent: '',
        lastUpdated: '',
        businessPolicyContent: '',
        businessPolicyLastUpdated: '',
    },
    homePage: {
        homePopupImage: '',
        homePopupIntervalHours: 8,
        sponsorLogos: [],
        categoryFilterIcons: [
            { id: 'all', title: 'สินค้าทั้งหมด', image: '' },
            { id: 'main', title: 'ฟิตติ้งAN3/เบรก', image: '' },
            { id: 'deals', title: 'ฟิตติ้งAN6-20 HYB', image: '' },
            { id: 'sets', title: 'เซ็ตสุดคุ้ม', image: '' },
            { id: 'exclusive', title: 'ท่อยาง/สายถัก', image: '' },
            { id: 'premium', title: 'ระบบน้ำมัน', image: '' },
            { id: 'bestseller', title: 'ขายดีประจำเดือน', image: '' },
            { id: 'new', title: 'สินค้าใหม่/PREMIUM LINE', image: '' },
        ],
        sponsorBrands: [
            { id: 'brand1', title: '', image: '' },
            { id: 'brand2', title: '', image: '' },
            { id: 'brand3', title: '', image: '' },
            { id: 'brand4', title: '', image: '' },
            { id: 'brand5', title: '', image: '' },
            { id: 'brand6', title: '', image: '' },
        ],
    },
    itemListPage: {
        defaultSort: 'popular',
        availableFilters: 'ฟิตติ้งAN3/เบรก\nฟิตติ้งAN6-20 HYB\nท่อยาง/สายถัก',
        showOrderCountLabel: 'ยอดสั่งซื้อ',
    },
    checkoutPage: {
        deliveryDiscount: 0,
        productDiscountPercent: 0,
        promptpayDiscountPercent: 0,
        mobileBankingDiscountPercent: 0,
        codDiscountPercent: 0,
        secretDealDiscountPercent: 0,
        tickerMessage: 'HYB MASTER SHOP พร้อมส่งความอร่อยถึงบ้านคุณ',
        baseShippingCost: 50,
        freeShippingThreshold: 1000,
        codWalletId: '',
        senderName: '',
        senderPhone: '',
        senderAddress: '',
    },
    historyPage: {
        statusUpdateHour: 20,
        archiveAfterDays: 10,
        repeatDealCopy: 'กดสั่งซื้ออีกครั้งเพื่อรับส่วนลดพิเศษสำหรับสมาชิก!',
    },
    myPage: {
        sponsorCarousel: [],
        companyAddress: '',
        companyPhone: '',
        supportEmail: '',
    },
    myLevelPage: {
        earnPointsPerSpend: 1,  // Global: spend 1 baht
        earnPointsAmount: 1,       // Global: get 1 point
        levels: [
            { level: 1, name: 'bronze', nameTh: 'ทองแดง', minPoints: 0, discountPercent: 0 },
            { level: 2, name: 'silver', nameTh: 'เงิน', minPoints: 1000, discountPercent: 2 },
            { level: 3, name: 'gold', nameTh: 'ทอง', minPoints: 5000, discountPercent: 5 },
            { level: 4, name: 'platinum', nameTh: 'แพลตินัม', minPoints: 15000, discountPercent: 10 },
            { level: 5, name: 'diamond', nameTh: 'เพชร', minPoints: 50000, discountPercent: 15 },
        ],
    },
    walletTermPage: {
        walletTerms: '',
        minimumTopup: 100,
        supportContact: 'ติดต่อ HYB Support ผ่าน LINE @hyb-support',
        topupTiers: [
            { amount: 100, credit: 110 },
            { amount: 200, credit: 220 },
            { amount: 500, credit: 600 },
        ],
        tickerMessage: '',
    },
};

/**
 * Clone default values (deep copy)
 */
export function cloneDefaultValues(): AppPageSettingsFormValues {
    return JSON.parse(JSON.stringify(DEFAULT_PAGE_SETTINGS)) as AppPageSettingsFormValues;
}

/**
 * SECTION_DEFINITIONS - SSOT for UI section grouping
 * Each page's fields are grouped into visual sections
 */
export const SECTION_DEFINITIONS: Partial<Record<PageKey, SectionConfig[]>> = {
    // FirstPage - Simple branding section
    firstPage: [
        {
            id: 'branding',
            icon: Palette,
            title: 'Branding Assets',
            description: 'โลโก้และภาพพื้นหลังสำหรับหน้าเข้าสู่ระบบ',
            fields: ['logoImage', 'backgroundImage'],
        },
    ],

    // TermPage - Terms content section
    termPage: [
        {
            id: 'terms-content',
            icon: ScrollText,
            title: 'เงื่อนไขการให้บริการ',
            fields: ['termsContent', 'lastUpdated'],
        },
        {
            id: 'business-policy',
            icon: ShieldCheck,
            title: 'นโยบายทางธุรกิจ',
            fields: ['businessPolicyContent', 'businessPolicyLastUpdated'],
        },
    ],

    // HomePage - 3 sections
    homePage: [
        {
            id: 'popup',
            icon: Megaphone,
            title: 'Popup แจ้งข่าว',
            description: 'ภาพ popup ที่แสดงเมื่อเข้าแอป',
            fields: ['homePopupImage', 'homePopupIntervalHours'],
        },
        {
            id: 'categories',
            icon: Grid3X3,
            title: 'หมวดหมู่สินค้า',
            description: 'ไอคอนแสดงหมวดสินค้า (8 หมวด)',
            fields: ['categoryFilterIcons'],
        },
        {
            id: 'sponsors',
            icon: Award,
            title: 'แบรนด์ผู้สนับสนุน',
            description: 'แสดงแบรนด์พาร์ทเนอร์ (3x2 Grid)',
            fields: ['sponsorBrands'],
        },
    ],

    // CheckoutPage - 5 sections (major redesign)
    checkoutPage: [
        {
            id: 'payment-discounts',
            icon: CreditCard,
            title: 'ส่วนลดตามช่องทางชำระเงิน',
            description: 'ส่วนลดเมื่อเลือกช่องทางชำระเงินต่างๆ',
            fields: ['promptpayDiscountPercent', 'mobileBankingDiscountPercent', 'codDiscountPercent'],
        },
        {
            id: 'product-discounts',
            icon: Tag,
            title: 'ส่วนลดสินค้า',
            description: 'ส่วนลดท้ายบิลและดีลลับ',
            fields: ['productDiscountPercent', 'secretDealDiscountPercent'],
        },
        {
            id: 'shipping',
            icon: Truck,
            title: 'การจัดส่ง',
            description: 'ค่าจัดส่งและเงื่อนไขส่งฟรี',
            fields: ['baseShippingCost', 'deliveryDiscount', 'freeShippingThreshold'],
        },
        {
            id: 'sender-info',
            icon: Package,
            title: 'ข้อมูลผู้ส่ง (ใบปะหน้าพัสดุ)',
            description: 'ข้อมูลที่พิมพ์บนใบปะหน้าพัสดุ',
            fields: ['senderName', 'senderPhone', 'codWalletId', 'senderAddress'],
        },
        {
            id: 'announcements',
            icon: Megaphone,
            title: 'ข้อความประกาศ',
            description: 'อักษรวิ่งที่แสดงท้ายหน้า Checkout',
            fields: ['tickerMessage'],
        },
    ],

    // HistoryPage - 2 sections
    historyPage: [
        {
            id: 'status-update',
            icon: Clock,
            title: 'การอัปเดตสถานะ',
            description: 'เวลาอัปเดตเลขพัสดุและการย้ายเข้าคลัง',
            fields: ['statusUpdateHour', 'archiveAfterDays'],
        },
        {
            id: 'promotions',
            icon: Gift,
            title: 'โปรโมชันซื้อซ้ำ',
            description: 'ข้อความโปรโมทดีลลับ',
            fields: ['repeatDealCopy'],
        },
    ],

    // MyPage - 2 sections
    myPage: [
        {
            id: 'sponsors-carousel',
            icon: Award,
            title: 'โลโก้ผู้สนับสนุน',
            description: 'รูปที่เลื่อนวนบนหน้า MY Page',
            fields: ['sponsorCarousel'],
        },
        {
            id: 'contact-info',
            icon: Phone,
            title: 'ติดต่อเรา',
            description: 'ข้อมูลบริษัทสำหรับการ์ด "ติดต่อเรา"',
            fields: ['companyAddress', 'companyPhone', 'supportEmail'],
        },
    ],

    // MyLevelPage - 2 sections
    myLevelPage: [
        {
            id: 'earn-rate',
            icon: Coins,
            title: 'อัตราการสะสมแต้ม',
            description: 'กำหนดจำนวนเงินต่อแต้มที่ได้รับ',
            fields: ['earnPointsPerSpend', 'earnPointsAmount'],
        },
        {
            id: 'levels',
            icon: Medal,
            title: 'ระดับสมาชิก',
            description: 'กำหนดเกณฑ์เลื่อนระดับและส่วนลด',
            fields: ['levels'],
        },
    ],

    // WalletTermPage - 3 sections
    walletTermPage: [
        {
            id: 'topup',
            icon: Wallet,
            title: 'การเติมเงิน',
            description: 'ขั้นต่ำและแพ็กเกจเติมเงิน',
            fields: ['minimumTopup', 'topupTiers'],
        },
        {
            id: 'terms',
            icon: ScrollText,
            title: 'ข้อกำหนดและเงื่อนไข',
            fields: ['walletTerms'],
        },
        {
            id: 'contact-support',
            icon: MessageSquare,
            title: 'ช่องทางติดต่อ & ประกาศ',
            fields: ['supportContact', 'tickerMessage'],
        },
    ],
};
